(() => {
  const VERSION = 1;
  const SINGLETON_KEY = '__eusariNearViewportLoader';
  const existing = window[SINGLETON_KEY];

  if (existing?.version === VERSION) {
    existing.init(document);
    return;
  }

  existing?.destroy?.();

  const ROOT_SELECTOR = '[data-eusari-near-viewport][data-eusari-near-viewport-src]';
  const ACTION_SELECTOR = '[data-eusari-near-viewport-action-src]';
  const LEAD_MARGIN = '1200px 0px';
  const FALLBACK_DELAY = 4000;
  const scripts = new Map();
  const loadedScripts = new Set();
  const rootStates = new WeakMap();
  const pendingActions = new Set();
  const replayingActions = new WeakSet();
  const controller = new AbortController();
  let observer = null;
  let fallbackTimer = 0;
  let destroyed = false;
  let generation = 0;

  const isActive = (activeGeneration) => !destroyed && activeGeneration === generation;

  const isDesignMode = () => Boolean(window.Shopify?.designMode);

  const loadScript = (url) => {
    if (scripts.has(url)) return scripts.get(url);

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.dataset.eusariNearViewportLoader = String(VERSION);
      script.addEventListener('load', () => {
        loadedScripts.add(url);
        resolve();
      }, { once: true });
      script.addEventListener('error', () => {
          scripts.delete(url);
          script.remove();
          reject(new Error(`Unable to load deferred module: ${url}`));
        }, { once: true });
      document.head.append(script);
    });

    scripts.set(url, promise);
    return promise;
  };

  const loadRoot = (root) => {
    if (destroyed || !root?.isConnected || rootStates.has(root)) return;
    const url = root.getAttribute('data-eusari-near-viewport-src');
    if (!url) return;

    rootStates.set(root, 'loading');
    root.dataset.eusariNearViewportState = 'loading';
    observer?.unobserve(root);
    const loadGeneration = generation;
    loadScript(url).then(() => {
      if (!isActive(loadGeneration)) return;
      rootStates.set(root, 'loaded');
      root.dataset.eusariNearViewportState = 'loaded';
    }).catch(() => {
      if (!isActive(loadGeneration)) return;
      rootStates.delete(root);
      root.dataset.eusariNearViewportState = 'error';
    });
  };

  const discover = (scope = document) => {
    const roots = new Set([
      ...(scope.matches?.(ROOT_SELECTOR) ? [scope] : []),
      ...(scope.querySelectorAll ? scope.querySelectorAll(ROOT_SELECTOR) : []),
    ]);

    roots.forEach((root) => {
      if (!root.isConnected || rootStates.has(root)) return;
      if (isDesignMode() || !('IntersectionObserver' in window)) loadRoot(root);
      else observer.observe(root);
    });
  };

  const loadDiscoveredRoots = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(loadRoot);
  };

  const startFallback = () => {
    window.clearTimeout(fallbackTimer);
    fallbackTimer = window.setTimeout(loadDiscoveredRoots, FALLBACK_DELAY);
  };

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) loadRoot(entry.target);
      });
    }, { rootMargin: LEAD_MARGIN, threshold: 0 });
  }

  const actionSourceFor = (trigger) => trigger?.getAttribute('data-eusari-near-viewport-action-src');

  const prewarmAction = (trigger) => {
    const url = actionSourceFor(trigger);
    if (url) void loadScript(url).catch(() => {});
  };

  const loadForIntent = (event) => {
    const action = event.target.closest?.(ACTION_SELECTOR);
    if (action) prewarmAction(action);
    const root = event.target.closest?.(ROOT_SELECTOR);
    if (root) loadRoot(root);
  };

  const replayAction = (trigger) => {
    replayingActions.add(trigger);
    trigger.click();
    replayingActions.delete(trigger);
  };

  const loadForActionClick = (event) => {
    if (destroyed) return;
    const trigger = event.target.closest?.(ACTION_SELECTOR);
    if (!trigger || replayingActions.has(trigger)) return;

    const url = actionSourceFor(trigger);
    if (!url || loadedScripts.has(url)) return;

    event.preventDefault();
    if (pendingActions.has(trigger)) return;

    pendingActions.add(trigger);
    trigger.setAttribute('aria-busy', 'true');
    const actionGeneration = generation;
    loadScript(url).then(() => {
      if (!isActive(actionGeneration)) return;
      pendingActions.delete(trigger);
      trigger.removeAttribute('aria-busy');
      if (trigger.isConnected) replayAction(trigger);
    }).catch(() => {
      if (!isActive(actionGeneration)) return;
      pendingActions.delete(trigger);
      trigger.removeAttribute('aria-busy');
    });
  };

  ['focusin', 'pointerenter', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, loadForIntent, {
      capture: eventName !== 'touchstart',
      passive: eventName === 'touchstart',
      signal: controller.signal,
    });
  });
  document.addEventListener('click', loadForActionClick, { capture: true, signal: controller.signal });
  document.addEventListener('shopify:section:load', (event) => {
    discover(event.target);
    if (isDesignMode()) startFallback();
  }, { signal: controller.signal });

  window.addEventListener('load', startFallback, { once: true, signal: controller.signal });

  window[SINGLETON_KEY] = {
    version: VERSION,
    init: discover,
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      generation += 1;
      controller.abort();
      observer?.disconnect();
      window.clearTimeout(fallbackTimer);
      pendingActions.forEach((trigger) => trigger.removeAttribute('aria-busy'));
      pendingActions.clear();
    },
  };

  discover();
  if (document.readyState === 'complete') startFallback();
})();
